import { TourStep } from '../../common/GuidedTour/GuidedTour';

/**
 * Tour steps for the Revive Flow page guided tour.
 * This walks first-time users through creating their first Revive flow.
 *
 * Pattern: Each wizard step has two tour steps - one for the input, one for the Next button
 * Wizard Steps: Name -> Description -> Transfer Line -> Schedule -> Summary
 */
export const reviveTourSteps: TourStep[] = [
  // Step 0: Click New Flow button to open wizard
  {
    target: 'revive-new-flow-button',
    title: '👆 Create Your First Flow',
    content: 'Click this button to create your first Revive flow.',
    position: 'bottom',
    waitForClick: true,
    clickText: 'Click "New Revive Flow" to continue',
  },
  // Step 1: Enter flow name
  {
    target: 'flow-name-input',
    title: '✏️ Name Your Flow',
    content:
      'Enter a unique name for your flow (e.g., "Q1 Dormant Leads"). This helps you identify it later.',
    position: 'bottom',
    allowInteraction: true,
  },
  // Step 2: Click Next after name
  {
    target: 'modal-next-button',
    title: '➡️ Continue to Description',
    content: 'Click Next to proceed to the description step.',
    position: 'top',
    waitForClick: true,
    clickText: 'Click "Next" to continue',
  },
  // Step 3: Enter description
  {
    target: 'flow-description-input',
    title: '📝 Describe Your Flow',
    content:
      'Describe what this flow is about. The AI will use this to have better conversations with your leads.',
    position: 'bottom',
    allowInteraction: true,
  },
  // Step 4: Click Next after description
  {
    target: 'modal-next-button',
    title: '➡️ Continue to Transfer Lines',
    content: 'Click Next to select who will receive transferred calls.',
    position: 'top',
    waitForClick: true,
    clickText: 'Click "Next" to continue',
  },
  // Step 5: Click Add New Team button
  {
    target: 'add-team-member-button',
    title: '👥 Invite a Team Member',
    content:
      'Click here to invite a team member who will receive transferred calls when a lead is interested.',
    position: 'bottom',
    waitForClick: true,
    clickText: 'Click "Add New Team" to continue',
  },
  // Step 6: Fill out team member form
  {
    target: 'team-member-form',
    title: '📧 Add Team Member Details',
    content:
      'Fill in the team member\'s details. At minimum, enter their email address. Click "Add Member" when done.',
    position: 'top',
    allowInteraction: true,
  },
  // Step 7: Click Next after transfer line
  {
    target: 'modal-next-button',
    title: '➡️ Continue to Schedule',
    content: 'Select the invited team member from the dropdown, then click Next to set up your flow schedule.',
    position: 'top',
    waitForClick: true,
    clickText: 'Click "Next" to continue',
  },
  // Step 8: Schedule date/time
  {
    target: 'schedule-datetime',
    title: '📅 Schedule Your Flow',
    content:
      'Set when your flow should start. Choose a date and preferred time for the first calls to go out.',
    position: 'bottom',
    allowInteraction: true,
  },
  // Step 9: Advanced Settings - informational only (like skip adding contacts)
  {
    target: '', // Not used - skipTarget bypasses target finding
    title: '⚙️ Skip Advanced Settings',
    content:
      'You can customize advanced options like scheduling window, allowed days/hours, and holiday exclusions. You can configure these later - click "Next" to continue.',
    position: 'top',
    skipTarget: true, // Shows centered tooltip with full blocking overlay (no interaction)
  },
  // Step 10: Click Next after schedule
  {
    target: 'modal-next-button',
    title: '➡️ Review Your Settings',
    content: 'Click Next to review your flow settings before creating.',
    position: 'top',
    waitForClick: true,
    clickText: 'Click "Next" to continue',
  },
  // Step 11: Summary review - highlights Flow Summary header and Flow Information card only
  {
    target: 'flow-summary-info',
    title: '📋 Review Your Settings',
    content:
      'This is where you review your flow settings. You can come back to this later to update.',
    position: 'top',
    allowInteraction: true,
  },
  // Step 12: Click Complete to create flow
  {
    target: 'modal-complete-button',
    title: '✅ Create Your Flow',
    content: 'Everything looks good? Click Complete to create your flow!',
    position: 'top',
    waitForClick: true,
    clickText: 'Click "Complete" to create your flow',
  },
  // Step 13: Flow building loading
  {
    target: 'flow-building-dialog',
    title: '⏳ Building Your Flow',
    content: 'Please wait while we set up your flow. This will only take a moment...',
    position: 'bottom',
    allowInteraction: false,
  },
  // Step 14: Add contacts modal - skip for now (no interaction allowed, no highlight)
  {
    target: '', // Not used - skipTarget bypasses target finding
    title: '👥 Skip Adding Contacts For Now',
    content: 'You can add contacts to your flow later. Click "Next" to continue.',
    position: 'top',
    skipTarget: true, // Shows centered tooltip with full blocking overlay
  },
  // Step 15: Edit flow description
  {
    target: 'edit-flow-description',
    title: '✏️ Review Your Description',
    content:
      'This is the description you entered earlier. You can edit it here to customize how Remi will communicate with your leads. Click "Next" when ready.',
    position: 'top',
    allowInteraction: true,
  },
  // Step 16: Edit flow voicemail
  {
    target: 'edit-flow-voicemail',
    title: '📞 Customize Your Voicemail',
    content:
      'You can edit your voicemail or click "Regenerate Voicemail" to to create new script based on your flow description. Click "Next" when ready.',
    position: 'top',
    allowInteraction: true,
  },
  // Step 17: Test call section - select contact OR add new one
  {
    target: 'test-call-section',
    title: '🎉 Your Flow is Ready!',
    content:
      'Your flow has been created! Select a test contact from the dropdown. ⚠️ If you see an error (same as transfer line), click the ➕ button to add a different test contact.',
    position: 'top',
    allowInteraction: true, // Allows dropdown AND add button interaction
  },
  // Step 18: Add test contact form (controlled by parent - shown when add button is clicked)
  {
    target: 'add-test-contact-form',
    title: '📝 Add Your Test Contact',
    content:
      'Enter your name and phone number. ⚠️ Make sure to use a DIFFERENT number than the transfer line you set up earlier. Click "Add Contact" when done, then select it from the dropdown.',
    position: 'right',
    allowInteraction: true,
    // Note: skipIfNotFound removed - step is now controlled by parent and only shown when add modal is open
  },
  // Step 19: Test call button (final step)
  {
    target: 'test-call-button',
    title: '🚀 Make a Test Call',
    content:
      "Click 'Test Call' to try out your flow! Remi will call your phone number. The guide is now complete - you're ready to use Revive Flows!",
    position: 'top',
    allowInteraction: true, // Allow clicking the test call button with overlay
  },
];
